import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from '../../app/store'
import { openShift, setHasActiveShift } from '../../features/shift/shiftSlice'
import toast from 'react-hot-toast'
import {
  LiftPage,
  NumericKeyboard,
  FlutterButton,
  AmountField
} from '../../components/auth'

const ShiftOpeningPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  
  const { isLoading, hasActiveShift } = useSelector((state: RootState) => state.shift)
  const { settings } = useSelector((state: RootState) => state.settings)
  
  const [amount, setAmount] = useState('')
  const shiftOpenedRef = useRef(false)

  const isRTL = settings.language === 'ar'

  // Prevent scrolling and manage body class
  useEffect(() => {
    document.body.classList.add('no-scroll')
    
    return () => {
      document.body.classList.remove('no-scroll')
      shiftOpenedRef.current = false
    }
  }, [])

  // Navigate to home when shift is successfully opened
  useEffect(() => {
    if (hasActiveShift && !isLoading && shiftOpenedRef.current) {
      toast.success('تم فتح الشيفت بنجاح', {
        icon: '✓',
        duration: 2000,
        style: {
          background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
          color: '#fff',
          fontSize: '17px',
          fontFamily: 'IBM Plex Sans Arabic, sans-serif',
          direction: 'rtl',
          padding: '16px 24px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
          fontWeight: '500',
          letterSpacing: '0.3px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#4CAF50',
        },
      })
      navigate('/home', { replace: true })
    }
  }, [hasActiveShift, isLoading, navigate])

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) < 0) {
      toast.error(t('validation.required_field'))
      return
    }

    // Validate decimal places (max 2)
    if (amount.includes('.')) {
      const parts = amount.split('.')
      if (parts.length > 1 && parts[1].length > 2) {
        toast.error(t('validation.max_decimal_places'))
        return
      }
    }

    try {
      shiftOpenedRef.current = true
      const resultAction = await dispatch(openShift({ openingBalance: amount }) as any)
      
      // Check multiple ways to ensure we catch success
      const isFulfilled = 
        openShift.fulfilled.match(resultAction) ||
        resultAction?.type === 'shift/openShift/fulfilled' ||
        (resultAction?.meta?.requestStatus === 'fulfilled' && !resultAction?.error)
      
      const isRejected = 
        openShift.rejected.match(resultAction) ||
        resultAction?.type === 'shift/openShift/rejected' ||
        (resultAction?.meta?.requestStatus === 'rejected') ||
        resultAction?.error
      
      // Check if payload indicates success (even if action was rejected)
      const payloadIsSuccess = 
        resultAction?.payload === 'success' ||
        (typeof resultAction?.payload === 'object' && resultAction?.payload?.success === true) ||
        (typeof resultAction?.payload === 'string' && resultAction?.payload.toLowerCase().includes('success'))
      
      if (isFulfilled && !isRejected) {
        // Navigation will be handled by useEffect when hasActiveShift changes
      } else if (isRejected && payloadIsSuccess) {
        // API returned success but action was rejected - treat as success
        // Manually update state and navigate
        dispatch(setHasActiveShift(true))
        localStorage.setItem('has_active_shift', 'true')
        toast.success('تم فتح الشيفت بنجاح', {
          icon: '✓',
          duration: 2000,
          style: {
            background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
            color: '#fff',
            fontSize: '17px',
            fontFamily: 'IBM Plex Sans Arabic, sans-serif',
            direction: 'rtl',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
            fontWeight: '500',
            letterSpacing: '0.3px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
          iconTheme: {
            primary: '#fff',
            secondary: '#4CAF50',
          },
        })
        navigate('/home', { replace: true })
      } else if (isRejected) {
        shiftOpenedRef.current = false
        const errorMessage = resultAction.payload as string || t('shift.error.open_failed')
        toast.error(errorMessage)
      } else {
        // Fallback: if we have payload and no error, assume success
        if (resultAction?.payload && !resultAction?.error) {
          // Navigation will be handled by useEffect when hasActiveShift changes
        } else {
          shiftOpenedRef.current = false
          toast.error(t('shift.error.open_failed'))
        }
      }
    } catch (error: any) {
      shiftOpenedRef.current = false
      toast.error(error?.message || t('shift.error.open_failed'))
    }
  }

  const addNumber = (number: string) => {
    if (number === '.' && amount.includes('.')) return
    setAmount(prev => prev + number)
  }

  const deleteNumber = () => {
    setAmount(prev => prev.slice(0, -1))
  }

  return (
    <div className="h-screen bg-[#F5F5F5] flex overflow-hidden">
      {/* Main content area */}
      <div className="flex-1 flex flex-col items-center justify-center" style={{ direction: 'ltr' }}>
        {/* Title */}
        <h1 
          className="text-black font-medium mb-[clamp(12px,1.5vw,16px)] text-[clamp(20px,2.2vw,32px)] font-['IBM Plex Sans Arabic']"
          style={{ textAlign: isRTL ? 'right' : 'left' }}
        >
          {t('shift.enter_custody_amount')}
        </h1>
        
        {/* Container for consistent width */}
        <div className="w-full max-w-[clamp(320px,65vw,580px)] flex flex-col items-center px-[clamp(12px,1.5vw,16px)]">
          {/* Amount Field */}
          <div className="w-full mb-[clamp(8px,1vw,12px)]">
            <AmountField value={amount} />
          </div>
          
          {/* Spacing */}
          <div className="h-[clamp(4px,0.5vw,8px)]"></div>
          
          {/* Keyboard */}
          <div className="w-full mb-[clamp(12px,1.5vw,16px)]">
            <NumericKeyboard 
              onNumberPress={addNumber}
              onDeletePress={deleteNumber}
              disabled={isLoading}
              includeDecimal={true}
            />
          </div>
          
          {/* Spacing */}
          <div className="h-[clamp(8px,1vw,16px)]"></div>
          
          {/* Button - aligned with keyboard width */}
          <div className="w-full">
            <FlutterButton
              text={t('common.done')}
              onPress={handleSubmit}
              disabled={amount.length === 0}
              isLoading={isLoading}
              loadingText={t('common.loading')}
            />
          </div>
        </div>
      </div>   
      {/* Lift page */}
      <LiftPage />
    </div>
  )
}

export default ShiftOpeningPage
